import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Calendar, Clock, MapPin } from 'lucide-react';

export const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    departure: '',
    destination: '',
    date: '',
    time: '',
    passengers: '1',
    baggage: '1',
    serviceType: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch('https://formspree.io/f/mjgebozo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          departure: formData.departure,
          destination: formData.destination,
          date: formData.date,
          time: formData.time,
          passengers: formData.passengers,
          baggage: formData.baggage,
          serviceType: formData.serviceType,
          message: formData.message,
          _subject: `Nouvelle demande VTC - ${formData.name}`,
          _replyto: formData.email,
          details: `
NOUVELLE DEMANDE DE DEVIS VTC

👤 Client: ${formData.name}
📧 Email: ${formData.email}  
📞 Téléphone: ${formData.phone}

🚗 TRAJET:
📍 Départ: ${formData.departure}
📍 Destination: ${formData.destination}
📅 Date: ${formData.date}
🕐 Heure: ${formData.time}

👥 Passagers: ${formData.passengers}
🧳 Bagages: ${formData.baggage}
🎯 Type de service: ${formData.serviceType}

💬 Message: ${formData.message || 'Aucun message'}
          `
        })
      });

      if (response.ok) {
        setSubmitted(true);
        console.log('Demande envoyée via Formspree');
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setError('Erreur lors de l\'envoi de votre demande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <Card className="max-w-md mx-auto bg-gray-800/90 border-gray-700">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-600 text-2xl">✓</span>
          </div>
          <h3 className="text-xl font-semibold mb-2 text-white">Demande envoyée !</h3>
          <p className="text-gray-300">Nous vous recontacterons rapidement pour confirmer votre réservation.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto bg-gray-800/90 border-gray-700">
      <CardHeader>
        <CardTitle className="text-2xl text-center text-white">Demander un Devis</CardTitle>
        <CardDescription className="text-center text-gray-300">
          Remplissez ce formulaire pour recevoir votre devis personnalisé
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Nom complet *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-300">Téléphone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="departure">Lieu de départ *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  id="departure"
                  name="departure"
                  className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                  value={formData.departure}
                  onChange={(e) => handleChange('departure', e.target.value)}
                  placeholder="Adresse de prise en charge"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="destination">Destination *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  id="destination"
                  name="destination"
                  className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                  value={formData.destination}
                  onChange={(e) => handleChange('destination', e.target.value)}
                  placeholder="Adresse de destination"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  id="date"
                  name="date"
                  type="date"
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="text-gray-300">Heure *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  id="time"
                  name="time"
                  type="time"
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                  value={formData.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="passengers" className="text-gray-300">Passagers</Label>
              <select
                name="passengers"
                value={formData.passengers}
                onChange={(e) => handleChange('passengers', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 text-white rounded-md"
              >
                <option value="1">1 personne</option>
                <option value="2">2 personnes</option>
                <option value="3">3 personnes</option>
                <option value="4">4 personnes</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="baggage" className="text-gray-300">Nombre de bagages</Label>
              <select
                name="baggage"
                value={formData.baggage}
                onChange={(e) => handleChange('baggage', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 text-white rounded-md"
              >
                <option value="0">Aucun bagage</option>
                <option value="1">1 bagage</option>
                <option value="2">2 bagages</option>
                <option value="3">3 bagages</option>
                <option value="4">4 bagages</option>
                <option value="5+">5 bagages ou plus</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="serviceType" className="text-gray-300">Type de service</Label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={(e) => handleChange('serviceType', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 text-white rounded-md"
              >
                <option value="">Sélectionnez le type de service</option>
                <option value="aeroport">Transfert Aéroport</option>
                <option value="gare">Transfert Gare</option>
                <option value="tourisme">Lieu Touristique</option>
                <option value="medical">Établissement Médical</option>
                <option value="disposition">Mise à Disposition</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-300">Message (optionnel)</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Informations complémentaires..."
              rows={3}
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
            />
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};